/**
 * Step 4 — evidence and badge (`GET /verification/moderator/companies/<id>/badges/`).
 *
 * Evidence facts hold or not (derived server-side from reviewed documents and
 * decided checks, by what each check covered). A badge can be awarded only
 * when every fact it requires holds; a super moderator may override with a
 * recorded reason. The public badge pauses when its evidence lapses.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EMPTY_VALUE, formatDate } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import { AWARD_STATE_CHIP, VERDICT_CHIP } from "@/lib/kyb";
import type { _BadgeOption, CompanyBadgeAward, CompanyBadgePanel } from "@/services/moderators/generated/model";
import {
  useVerificationModeratorAwardsRevokeCreate,
  useVerificationModeratorCompaniesAwardBadgeCreate,
} from "@/services/moderators/generated/moderators-verification/moderators-verification";

export const KybBadgePanel = ({ panel, onChanged }: { panel: CompanyBadgePanel; onChanged: () => void }) => {
  const { t } = useTranslation(["kyb", "common"]);
  const factLabel = (key: string) => panel.facts.find((f) => f.key === key)?.label ?? key;
  const holds = new Set(panel.evidence);
  const publicAward = panel.publicAward;
  const badges = [...panel.badges].filter((b) => b.isActive).sort((a, b) => b.rank - a.rank);

  return (
    <>
      <section className="kyb-section">
        <h2 className="kyb-section__title">{t("badges.evidenceTitle")}</h2>
        <p className="kyb-section__help">{t("badges.evidenceHelp")}</p>
        <ul className="kyb-facts">
          {panel.facts.map((fact) => {
            const ok = holds.has(fact.key);
            return (
              <li key={fact.key} className={`kyb-fact ${ok ? "kyb-fact--holds" : "kyb-fact--missing"}`}>
                <span className="kyb-fact__icon">
                  <i className={ok ? "fa-solid fa-circle-check" : "fa-regular fa-circle"} />
                </span>
                <span>{fact.label}</span>
                <span className="kyb-note">{ok ? t("badges.holds") : t("badges.missing")}</span>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="kyb-section">
        <h2 className="kyb-section__title">
          <span className="mod-page__chip">4</span> {t("badges.title")}
        </h2>
        <p className="kyb-section__help">{t("badges.help")}</p>
        <div className="mod-page__form-row">
          <span className={`mod-page__chip ${VERDICT_CHIP[panel.verdict]}`}>
            {t("verdict.prefix", { verdict: t(`verdict.${panel.verdict}`) })}
          </span>
          {publicAward ? (
            <span className="kyb-badge">
              <i className="fa-solid fa-shield-halved" /> {publicAward.badgeLabel}
            </span>
          ) : (
            <span className="kyb-badge kyb-badge--none">{t("badges.noPublicBadge")}</span>
          )}
        </div>
        <p className="kyb-note">{t(`verdict.help.${panel.verdict}`)}</p>
        {publicAward ? (
          <p className="kyb-note">
            {t("badges.validUntil", { date: formatDate(publicAward.validUntil) })}
            {publicAward.awardedBy ? ` · ${t("badges.awardedBy", { name: publicAward.awardedBy.name })}` : ""}
          </p>
        ) : null}

        <div className="kyb-sub">
          {badges.map((badge) => (
            <BadgeOption
              key={badge.key}
              companyId={panel.companyId}
              badge={badge}
              suggested={badge.key === panel.suggestedBadge}
              isPublic={publicAward?.badge === badge.key}
              canOverride={panel.canOverride}
              factLabel={factLabel}
              holds={holds}
              onAwarded={onChanged}
            />
          ))}
          {badges.length === 0 ? (
            <div className="kyb-callout kyb-callout--warning">{t("badges.noneSeeded")}</div>
          ) : null}
        </div>
      </section>

      <AwardHistory awards={panel.awards} onChanged={onChanged} />
    </>
  );
};

const BadgeOption = ({
  companyId,
  badge,
  suggested,
  isPublic,
  canOverride,
  factLabel,
  holds,
  onAwarded,
}: {
  companyId: number;
  badge: _BadgeOption;
  suggested: boolean;
  isPublic: boolean;
  canOverride: boolean;
  factLabel: (key: string) => string;
  holds: Set<string>;
  onAwarded: () => void;
}) => {
  const { t } = useTranslation(["kyb", "common"]);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const award = useVerificationModeratorCompaniesAwardBadgeCreate({
    mutation: {
      onSuccess: () => {
        setOpen(false);
        setNote("");
        setOverrideReason("");
        onAwarded();
      },
    },
  });
  const needsOverride = !badge.awardable;
  const canAward = badge.awardable || canOverride;

  return (
    <div className={`kyb-badge-option ${suggested ? "kyb-badge-option--suggested" : ""}`}>
      <div className="kyb-provider__head">
        <div>
          <strong>{badge.label}</strong>{" "}
          {suggested ? <span className="mod-page__chip mod-page__chip--success">{t("badges.suggested")}</span> : null}
          {isPublic ? <span className="mod-page__chip mod-page__chip--success">{t("badges.current")}</span> : null}
          <div className="kyb-provider__tags">
            {badge.requiredEvidence.map((fact) => (
              <span
                key={fact}
                className={`mod-page__chip ${holds.has(fact) ? "mod-page__chip--success" : "mod-page__chip--danger"}`}
              >
                <i className={holds.has(fact) ? "fa-solid fa-check" : "fa-solid fa-xmark"} /> {factLabel(fact)}
              </span>
            ))}
          </div>
        </div>
        {canAward && !isPublic ? (
          <button
            type="button"
            className={`mod-page__btn ${needsOverride ? "mod-page__btn--ghost" : "mod-page__btn--primary"}`}
            onClick={() => setOpen((v) => !v)}
          >
            {needsOverride ? t("badges.overrideAward") : t("badges.award")}
          </button>
        ) : null}
      </div>
      {!badge.awardable && !canOverride ? (
        <p className="kyb-note">
          {t("badges.blocked", { facts: badge.missingEvidence.map(factLabel).join(", ") || EMPTY_VALUE })}
        </p>
      ) : null}

      {open ? (
        <div className="kyb-form kyb-sub">
          {needsOverride ? <div className="kyb-callout kyb-callout--warning">{t("badges.overrideWarning")}</div> : null}
          <label>
            {t("badges.note")}
            <textarea className="mod-page__input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <label>
            {t("badges.validUntilLabel")}
            <input
              type="date"
              className="mod-page__input"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
            />
            <span className="kyb-note">{t("badges.validUntilHelp")}</span>
          </label>
          {needsOverride ? (
            <label>
              {t("badges.overrideReason")}
              <textarea
                className="mod-page__input"
                rows={2}
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
            </label>
          ) : null}
          <div className="mod-page__form-row">
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              disabled={award.isPending || (needsOverride && !overrideReason.trim())}
              onClick={() =>
                award.mutate({
                  id: companyId,
                  data: {
                    badge: badge.key,
                    note: note.trim() || undefined,
                    validUntil: validUntil ? new Date(`${validUntil}T23:59:59`).toISOString() : undefined,
                    overrideReason: needsOverride ? overrideReason.trim() : undefined,
                  },
                })
              }
            >
              {award.isPending ? t("badges.awarding") : t("badges.confirmAward", { label: badge.label })}
            </button>
            <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => setOpen(false)}>
              {t("common:actions.cancel")}
            </button>
          </div>
          {award.isError ? (
            <div className="mod-page__inline-error">{getApiErrorMessage(award.error, t("badges.awardError"))}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const AwardHistory = ({ awards, onChanged }: { awards: CompanyBadgeAward[]; onChanged: () => void }) => {
  const { t } = useTranslation(["kyb", "common"]);
  return (
    <section className="kyb-section">
      <h2 className="kyb-section__title">{t("badges.historyTitle", { count: awards.length })}</h2>
      {awards.length === 0 ? <p className="kyb-note">{t("badges.noHistory")}</p> : null}
      {awards.map((award) => (
        <AwardRow key={award.id} award={award} onChanged={onChanged} />
      ))}
    </section>
  );
};

const AwardRow = ({ award, onChanged }: { award: CompanyBadgeAward; onChanged: () => void }) => {
  const { t } = useTranslation(["kyb", "common"]);
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState("");
  const revoke = useVerificationModeratorAwardsRevokeCreate({
    mutation: {
      onSuccess: () => {
        setRevoking(false);
        onChanged();
      },
    },
  });
  const live = award.state === "active" || award.state === "paused";

  return (
    <div className="kyb-badge-option">
      <div className="kyb-provider__head">
        <div>
          <strong>{award.badgeLabel}</strong>{" "}
          <span className={`mod-page__chip ${AWARD_STATE_CHIP[award.state]}`}>{t(`badges.state.${award.state}`)}</span>
          {award.overrideReason ? (
            <span className="mod-page__chip mod-page__chip--warning">{t("badges.overridden")}</span>
          ) : null}
          <p className="kyb-note">
            {t("badges.awardedOn", {
              date: formatDate(award.awardedAt),
              name: award.awardedBy?.name ?? EMPTY_VALUE,
              until: formatDate(award.validUntil),
            })}
          </p>
          {award.state === "paused" ? <p className="kyb-note">{t("badges.pausedHelp")}</p> : null}
          {award.note ? <p className="kyb-note">{t("badges.noteLine", { note: award.note })}</p> : null}
          {award.overrideReason ? (
            <p className="kyb-note">{t("badges.overrideLine", { reason: award.overrideReason })}</p>
          ) : null}
          {award.revokedAt ? (
            <p className="kyb-note">
              {t("badges.revokedLine", {
                date: formatDate(award.revokedAt),
                name: award.revokedBy?.name ?? EMPTY_VALUE,
                reason: award.revokeReason,
              })}
            </p>
          ) : null}
        </div>
        {live ? (
          <button type="button" className="mod-page__btn mod-page__btn--danger" onClick={() => setRevoking((v) => !v)}>
            {t("badges.revoke")}
          </button>
        ) : null}
      </div>
      {revoking ? (
        <div className="kyb-form">
          <input
            className="mod-page__input"
            placeholder={t("badges.revokeReason")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="mod-page__form-row">
            <button
              type="button"
              className="mod-page__btn mod-page__btn--danger"
              disabled={!reason.trim() || revoke.isPending}
              onClick={() => revoke.mutate({ id: award.id, data: { reason: reason.trim() } })}
            >
              {t("badges.confirmRevoke")}
            </button>
          </div>
          {revoke.isError ? (
            <div className="mod-page__inline-error">{getApiErrorMessage(revoke.error, t("badges.revokeError"))}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
